import React, { Component } from 'react'
import {
  Input,
  InputNumber,
  Tooltip,
  Button,
  Popconfirm,
  Form,
  AutoComplete,
  Icon,
  Spin,
  Select,
  Alert,
  Modal,
  message
} from 'antd'
import _ from 'lodash'
import numeral from 'numeral'
import config from '../../config'
import PropTypes from 'prop-types'
import {
  arrayMove,
  convertToOption,
  removeCurrencyMarkup,
  sanitizeItem,
  handleItemCalcs,
  sanitizeEstimateSectionsForCloning
} from '../../util'
import EstimateSection from './EstimateSection'

const { TextArea } = Input
const { API_BASE } = config

const FormItem = Form.Item
const Option = Select.Option

const propTypes = {
  handleFireWrapUpEstimate: PropTypes.func.isRequired
}

class EstimateForm extends Component {
  constructor(props) {
    super(props)
    this.state = {
      chargedTemplate: false,
      templateOptions: convertToOption(this.props.templates),
      currentRevisionNumberPrint: 1,
      sectionName: null,
      modalSectionNameVisible: false,
      taxValue: null,
      markupValue: null,
      overheadValue: null
    }
  }

  handleTemplateChange = templateId => {
    const selectedItem = this.props.templates.find(
      row => parseInt(row.id, 0) === parseInt(templateId, 0)
    )
    if (selectedItem) {
      let changeTemplate = false
      // change the estimate structure
      if (this.props.currentId) {
        if (
          window.confirm(
            'Are you sure you want to change the estimate template this is based off of? By doing so you will lose all data currently on the screen.'
          )
        ) {
          console.log('change the estimate structure')
          changeTemplate = true
        }
      } else {
        console.log('mount the template structure')
        changeTemplate = true
      }

      const form = this.props.form
      form.setFieldsValue({
        estimate_template_id: selectedItem.id,
        overhead: selectedItem.overhead * 100,
        markup: selectedItem.markup * 100,
        tax: selectedItem.tax * 100,
        description: selectedItem.description
      })

      if (changeTemplate) {
        this.props.loadTemplateData(templateId)
      }
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const form = this.props.form
    if (this.props.currentId !== prevProps.currentId) {
      if (this.props.currentId === -1) {
        form.resetFields()
        this.setState({
          dataStructure: []
        })
      }
      if (this.props.currentId > 0) {
        this.props.loadEstimateData(this.props.currentId)
        const { estimateInfo } = this.props

        form.setFieldsValue({
          estimate_template_id: estimateInfo.estimate_template_id,
          overhead: estimateInfo.overhead,
          markup: estimateInfo.markup,
          tax: estimateInfo.tax,
          description: estimateInfo.description
        })
      }
    }
    // Sometimes the Sections might download sooner, other times it may be the Items.
    // So we perform the same action on both scenarios and make sure we have all
    // data before proceding.
    if (this.props.templateSections !== prevProps.templateSections) {
      this.processIncomingData('template', 'section')
    }
    if (this.props.templateItems !== prevProps.templateItems) {
      this.processIncomingData('template', 'item')
    }

    if (this.props.estimateSections !== prevProps.estimateSections) {
      this.processIncomingData('estimate', 'section')
    }
    if (this.props.estimateItems !== prevProps.estimateItems) {
      this.processIncomingData('estimate', 'item')
    }

    // do the form math on the first charge
    if (this.state.dataStructure !== prevState.dataStructure) {
      this.doFormMath()
    }

    // check the create/edit action
    if (this.props.wrapUpEstimate) {
      const form = this.props.form
      form.validateFields((err, values) => {
        if (err) {
          return
        }

        for (const section of this.state.dataStructure) {
          if (section.items) {
            for (const item of section.items) {
              if (!item.item_id) {
                this.props.handleFireWrapUpEstimate(false)
                message.error('Empty items found! Please select an item!')
                return
              }
            }
          }
        }

        // sanitize estimate data structure before save on clonning mode
        let estimateSections = []
        if (this.props.cloningMode) {
          estimateSections = sanitizeEstimateSectionsForCloning(this.state.dataStructure)
        } else {
          estimateSections = this.state.dataStructure
        }

        const estimate = {
          estimateSections: estimateSections,
          estimateData: values,
          estimateTotals: this.state.totals
        }
        this.props.handleWrapUpEstimate(estimate)
      })
    }

    // update the current version for printing
    if (prevProps.loadingRevision !== this.props.loadingRevision) {
      const { revisions } = this.props
      if (revisions) {
        this.setState({
          currentRevisionNumberPrint: revisions[revisions.length - 1].currentRevisionNumber
        })
      }
    }
  }

  processIncomingData = source => {
    // This will grab the template sections and items coming from props and add them to the grid.
    let sections = []
    let items = []
    if (source === 'template') {
      if (this.props.templateSections && this.props.templateItems) {
        sections = this.props.templateSections
        items = this.props.templateItems
      }
    }
    if (source === 'estimate') {
      if (this.props.estimateSections && this.props.estimateItems) {
        sections = this.props.estimateSections
        items = this.props.estimateItems
        this.props.form.resetFields()
      }
    }

    if (sections.length > 0) {
      if (sections.length > 0 && items.length > 0) {
        let groupedItems = sections.map((section, index) => {
          let sectionItem = section
          if (source === 'template') {
            section.items = _.map(
              _.filter(
                items,
                item => item.estimate_section_template_id === section.estimate_section_template_id
              ),
              item => _.omit(item, ['id'])
            )
          } else {
            section.items = _.filter(items, item => item.estimate_section_id === section.id)
          }
          if (source === 'template') {
            delete section.id
          }

          return sectionItem
        })

        // sort by order
        const sortedGroupedItems = _.sortBy(groupedItems, section => {
          const sortedItems = section.items
            ? _.sortBy(section.items, item => {
                return item.order
              })
            : []

          section.items = sortedItems

          return section.order
        })

        this.setState({
          chargedTemplate: true,
          dataStructure: sortedGroupedItems
        })
      }
    }
  }

  handleItemChange = (itemObj, itemIndex, sectionIndex) => {
    const { dataStructure } = this.state
    const { estimateInfo } = this.props

    if (itemObj.length > 0) {
      const item = this.props.items.find(item => {
        return item.name === itemObj[0].text
      })

      if (item) {
        const itemToInsert = { ...item }

        // sanitize the item to compose the list
        itemToInsert.item_id = item.id
        const currentElm = _.get(dataStructure, `${sectionIndex}.items.${itemIndex}`, undefined)
        if (currentElm !== undefined && currentElm !== '' && !currentElm.newElement) {
          itemToInsert.id = currentElm.id
        } else {
          delete itemToInsert.id
        }

        const form = this.props.form

        itemToInsert.tax = estimateInfo ? estimateInfo.tax : form.getFieldValue('tax') / 100
        itemToInsert.overhead = estimateInfo
          ? estimateInfo.overhead
          : form.getFieldValue('overhead') / 100
        itemToInsert.markup = estimateInfo
          ? estimateInfo.markup
          : form.getFieldValue('markup') / 100
        itemToInsert.remove_default_markup = true
        itemToInsert.remove_default_tax = true
        itemToInsert.remove_default_overhead = true

        dataStructure[sectionIndex].items[itemIndex] = itemToInsert
      } else {
        dataStructure[sectionIndex].items[itemIndex] = {
          tax: null,
          overhead: null,
          markup: null,
          description: null,
          newElement: true
        }
        console.log('no item')
      }
    }

    this.setState(
      {
        dataStructure
      },
      this.doFormMath()
    )
  }

  handleSectionDown = sectionIndex => {
    const sortedDataStructure = arrayMove(this.state.dataStructure, sectionIndex, sectionIndex + 1)

    this.setState({
      dataStructure: sortedDataStructure
    })
  }

  handleSectionUp = sectionIndex => {
    const sortedDataStructure = arrayMove(this.state.dataStructure, sectionIndex, sectionIndex - 1)

    this.setState({
      dataStructure: sortedDataStructure
    })
  }

  handleItemDown = (itemIndex, sectionIndex) => {
    const { dataStructure } = this.state

    const sortedItemDataStructure = arrayMove(
      dataStructure[sectionIndex].items,
      itemIndex,
      itemIndex + 1
    )

    dataStructure[sectionIndex].items = sortedItemDataStructure

    this.setState({
      dataStructure
    })
  }

  handleItemUp = (itemIndex, sectionIndex) => {
    const { dataStructure } = this.state

    const sortedItemDataStructure = arrayMove(
      dataStructure[sectionIndex].items,
      itemIndex,
      itemIndex - 1
    )

    dataStructure[sectionIndex].items = sortedItemDataStructure

    this.setState({
      dataStructure
    })
  }

  handleAddSection = () => {
    let { dataStructure } = this.state

    if (dataStructure) {
      dataStructure.push({
        name: 'Unnamed Section'
      })
    } else {
      dataStructure = [
        {
          name: 'Unnamed Section'
        }
      ]
    }

    this.setState({
      dataStructure,
      chargedTemplate: true
    })
  }

  handleAddItem = sectionIndex => {
    const { dataStructure } = this.state
    const { estimateInfo } = this.props

    const form = this.props.form

    const item = {
      tax: estimateInfo ? estimateInfo.tax : form.getFieldValue('tax') / 100,
      overhead: estimateInfo ? estimateInfo.overhead : form.getFieldValue('overhead') / 100,
      markup: estimateInfo ? estimateInfo.markup : form.getFieldValue('markup') / 100,
      remove_default_markup: true,
      remove_default_overhead: true,
      remove_default_tax: true
    }

    if (dataStructure[sectionIndex].items) {
      dataStructure[sectionIndex].items.push(item)
    } else {
      dataStructure[sectionIndex].items = [item]
    }

    this.setState({
      dataStructure
    })
  }

  handleItemColumnChange = (e, field, itemIndex, sectionIndex) => {
    const { dataStructure } = this.state
    let inputValue = null
    switch (field) {
      case 'description':
        inputValue = e.target.value
        break
      case 'quantity':
        inputValue = e
        break
      case 'cost':
        inputValue = e.replace('$', '')
        break
      case 'remove_default_tax':
      case 'remove_default_overhead':
      case 'remove_default_markup':
        inputValue = e.target.checked
        break
      default:
        inputValue = e / 100
        break
    }

    const item = dataStructure[sectionIndex].items[itemIndex]
    const newItem = { ...item, [field]: inputValue }
    if (/^remove_default_/.test(field)) {
      const newField = field.replace(/^remove_default_/, '')
      newItem[newField] = 0
    }
    dataStructure[sectionIndex].items[itemIndex] = newItem

    this.setState(
      {
        dataStructure
      },
      this.doFormMath
    )
  }

  handleSectionDelete = sectionIndex => {
    if (window.confirm('Are you sure you want to delete this SECTION?')) {
      let { dataStructure } = this.state

      const section = dataStructure[sectionIndex]

      // if this is a new section, simply remove it from the array
      if (section.id === undefined || section.id === '') {
        dataStructure.splice(sectionIndex, 1)
      } else {
        // since this is already existing in the database, mark it for deletion
        section.markedForDeletion = true
      }

      this.setState(
        {
          dataStructure
        },
        this.doFormMath()
      )
    }
  }

  handleItemDelete = (sectionIndex, itemIndex) => {
    if (window.confirm('Are you sure you want to delete this ITEM?')) {
      let { dataStructure } = this.state

      var item = dataStructure[sectionIndex].items[itemIndex]

      // if this is a new item, simply remove it from the array
      if (item.id === undefined || item.id === '') {
        dataStructure[sectionIndex].items.splice(itemIndex, 1)
      } else {
        // since this is already existing in the database, mark it for deletion
        item.markedForDeletion = true
      }

      this.setState(
        {
          dataStructure
        },
        this.doFormMath()
      )
    }
  }

  handleSectionNameChange = (e, sectionIndex) => {
    let { dataStructure } = this.state

    dataStructure[sectionIndex].name = e.target.value

    this.setState({
      dataStructure
    })
  }

  handleGeneralOverheadChange = value => {
    const { estimateInfo, changeContentData, form } = this.props
    const { dataStructure, overheadValue } = this.state
    value = value.replace('%', '')
    if (!form.isFieldTouched('overhead') || overheadValue === value) {
      return
    }

    if (window.confirm('Do you want to apply this OVERHEAD to every item?') && dataStructure) {
      dataStructure.forEach((section, sectionindex) => {
        if (section.items) {
          section.items.forEach((item, itemIndex) => {
            if (dataStructure[sectionindex].items[itemIndex].remove_default_overhead) {
              dataStructure[sectionindex].items[itemIndex].overhead = value / 100
            }
          })
        }
      })

      this.setState(
        {
          dataStructure,
          overheadValue: value
        },
        this.doFormMath()
      )
    } else {
      this.setState({
        overheadValue: value
      })
    }

    // change contentData
    if (estimateInfo) {
      changeContentData(value / 100, 'overhead')
    }
  }

  handleGeneralMarkupChange = value => {
    const { estimateInfo, form } = this.props
    const { dataStructure, markupValue } = this.state
    value = value.replace('%', '')
    if (!form.isFieldTouched('markup') || markupValue === value) {
      return
    }

    if (window.confirm('Do you want to apply this MARKUP to every item?') && dataStructure) {
      dataStructure.forEach((section, sectionindex) => {
        if (section.items) {
          section.items.forEach((item, itemIndex) => {
            if (dataStructure[sectionindex].items[itemIndex].remove_default_markup) {
              dataStructure[sectionindex].items[itemIndex].markup = value / 100
            }
          })
        }
      })

      this.setState(
        {
          dataStructure,
          markupValue: value
        },
        this.doFormMath()
      )
    } else {
      this.setState({
        markupValue: value
      })
    }

    // change contentData
    if (estimateInfo) {
      this.props.changeContentData(value / 100, 'markup')
    }
  }

  handleGeneralTaxChange = value => {
    const { estimateInfo, form } = this.props
    value = value.replace('%', '')
    const { dataStructure, taxValue } = this.state
    if (!form.isFieldTouched('tax') || value === taxValue) {
      return
    }

    if (window.confirm('Do you want to apply this TAX to every item?') && dataStructure) {
      dataStructure.forEach((section, sectionindex) => {
        if (section.items) {
          section.items.forEach((item, itemIndex) => {
            if (dataStructure[sectionindex].items[itemIndex].remove_default_tax) {
              dataStructure[sectionindex].items[itemIndex].tax = value / 100
            }
          })
        }
      })

      this.setState(
        {
          dataStructure,
          taxValue: value
        },
        this.doFormMath()
      )
    } else {
      this.setState({
        taxValue: value
      })
    }

    // change contentData
    if (estimateInfo) {
      this.props.changeContentData(value / 100, 'tax')
    }
  }

  handleSelectedTemplate = templateId => {
    if (!this.state.templateOptions) {
      return null
    }
    const selectedTemplate = this.state.templateOptions.find(
      template => template.value === templateId
    )
    return selectedTemplate ? selectedTemplate.text : null
  }

  handleDuplicateSection = () => {
    const { dataStructure, sectionName, modalSectionDuplicateIndex } = this.state
    let items = []
    let sectionToClone = dataStructure[modalSectionDuplicateIndex]

    for (const item of sectionToClone.items) {
      let clonedItem = _.clone(item)

      delete clonedItem.id
      delete clonedItem.estimate_id
      delete clonedItem.estimate_section_id
      delete clonedItem.estimate_template_id
      delete clonedItem.estimate_section_template_id

      items.push(clonedItem)
    }

    let newDataStructure = dataStructure

    let duplicatedSection = {
      name: sectionName,
      items: items,
      grand_total: sectionToClone.grand_total
    }

    newDataStructure.push(duplicatedSection)

    this.setState({
      dataStructure: newDataStructure,
      modalSectionNameVisible: false
    })
  }

  handleCancelSectionNameModal = () => {
    this.setState({
      modalSectionNameVisible: false
    })
  }

  handleOpenDuplicateSectionModal = index => {
    const { dataStructure } = this.state
    this.setState({
      sectionName: dataStructure[index].name,
      modalSectionNameVisible: true,
      modalSectionDuplicateIndex: index
    })
  }

  doFormMath = () => {
    // calculate the sections/items
    const { dataStructure } = this.state

    dataStructure.forEach((section, sectionIndex) => {
      if (!section.markedForDeletion && section.items && section.items.length > 0) {
        section.items.forEach((item, itemIndex) => {
          if (!item.markedForDeletion) {
            const itemSanitized = sanitizeItem(item)
            dataStructure[sectionIndex].items[itemIndex] = handleItemCalcs(itemSanitized)
          }
        })
      }
    })

    this.setState(
      {
        dataStructure
      },
      () => {
        // calculate the totals
        let total = 0.0
        let overhead_total = 0.0
        let markup_total = 0.0
        let tax_total = 0.0
        let grand_total = 0.0
        if (this.state.dataStructure) {
          this.state.dataStructure.forEach(section => {
            if (!section.markedForDeletion && section.items && section.items.length > 0) {
              section.items.forEach(item => {
                if (!item.markedForDeletion) {
                  total += +removeCurrencyMarkup(item.cost) * item.quantity
                  overhead_total += +removeCurrencyMarkup(item.overhead_total)
                  markup_total += +removeCurrencyMarkup(item.markup_total)
                  tax_total += +removeCurrencyMarkup(item.tax_total)
                  grand_total += +removeCurrencyMarkup(item.grand_total)
                }
              })
            }
          })
          this.setState({
            totals: {
              total,
              overhead_total,
              markup_total,
              tax_total,
              grand_total
            }
          })
        }
      }
    )
  }
  handleRevisionChange = revisionNumber => {
    const { form, revisions } = this.props

    const revision = revisions[revisionNumber]

    // set revision for printing
    this.setState({
      currentRevisionNumberPrint: revision.currentRevisionNumber
    })

    let changes = {}
    _.forEach(revision, (fieldValue, fieldName) => {
      if (fieldName === 'sections') {
        this.setState(prevState => {
          return { ...prevState, dataStructure: fieldValue }
        })
      }
      if (!_.isNil(form.getFieldsValue()[fieldName])) {
        if (fieldName === 'overhead' || fieldName === 'markup' || fieldName === 'tax') {
          changes[fieldName] = fieldValue * 100
        } else {
          changes[fieldName] = fieldValue.toString()
        }
      }
    })

    form.setFieldsValue(changes)
  }
  render() {
    const { getFieldDecorator } = this.props.form
    const { currentRevisionNumberPrint } = this.state
    const { items, estimateInfo, revisions, loadingRevision, cloningMode } = this.props
    return (
      <div>
        <Form>
          {cloningMode && (
            <Alert
              message="ATTENTION! You are about to clone an estimate"
              type="warning"
              showIcon
            />
          )}
          <div className="row">
            <div className="col-6">
              <FormItem label="Template">
                {getFieldDecorator('estimate_template_id', {
                  initialValue: this.handleSelectedTemplate(
                    estimateInfo && estimateInfo.estimate_template_id
                  )
                })(
                  <AutoComplete
                    dataSource={this.state.templateOptions}
                    filterOption={(inputValue, option) =>
                      option.props.children.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                    }
                    onSelect={templateId => this.handleTemplateChange(templateId)}
                  >
                    <Input suffix={<Icon type="search" className="certain-category-icon" />} />
                  </AutoComplete>
                )}
              </FormItem>
            </div>
            <div className="col-6">
              <div className="row estimate-main-inputs">
                <div className="col-4">
                  <Tooltip
                    placement="topLeft"
                    title="Changing this value will affect the overhead value in ALL items in this Estimate."
                  >
                    <FormItem label="Overhead">
                      {getFieldDecorator('overhead', {
                        initialValue: estimateInfo ? (estimateInfo.overhead * 100).toFixed(3) : null
                      })(
                        <InputNumber
                          className="min-width-input-small"
                          min={0}
                          precision={3}
                          step={0.1}
                          formatter={value => `${value}%`}
                          parser={value => value.replace('%', '')}
                          onBlur={e => this.handleGeneralOverheadChange(e.target.value)}
                        />
                      )}
                    </FormItem>
                  </Tooltip>
                </div>
                <div className="col-4">
                  <Tooltip
                    placement="topLeft"
                    title="Changing this value will affect the markup value in ALL items in this Estimate."
                  >
                    <FormItem label="Markup">
                      {getFieldDecorator('markup', {
                        initialValue: estimateInfo ? (estimateInfo.markup * 100).toFixed(3) : null
                      })(
                        <InputNumber
                          className="min-width-input-small"
                          min={0}
                          precision={3}
                          onBlur={e => this.handleGeneralMarkupChange(e.target.value)}
                          step={0.1}
                          formatter={value => `${value}%`}
                          parser={value => value.replace('%', '')}
                        />
                      )}
                    </FormItem>
                  </Tooltip>
                </div>
                <div className="col-4">
                  <Tooltip
                    placement="topLeft"
                    title="Changing this value will affect the tax value in ALL items in this Estimate."
                  >
                    <FormItem label="Tax">
                      {getFieldDecorator('tax', {
                        initialValue:
                          estimateInfo && estimateInfo ? (estimateInfo.tax * 100).toFixed(3) : null
                      })(
                        <InputNumber
                          className="min-width-input-small"
                          min={0}
                          max={100}
                          precision={3}
                          step={0.1}
                          formatter={value => `${value}%`}
                          parser={value => value.replace('%', '')}
                          onBlur={e => this.handleGeneralTaxChange(e.target.value)}
                        />
                      )}
                    </FormItem>
                  </Tooltip>
                </div>
              </div>
            </div>
          </div>
          {estimateInfo && (
            <div className="row">
              <div className="col-3">
                <FormItem label="Revision">
                  {loadingRevision ? (
                    <Spin size="small" />
                  ) : (
                    <Select
                      defaultValue={
                        revisions ? revisions[revisions.length - 1].currentRevisionNumber : null
                      }
                      onChange={this.handleRevisionChange}
                    >
                      {revisions &&
                        revisions.map(revision => (
                          <Option
                            key={revision.currentRevisionNumber}
                            value={revision.currentRevisionNumber - 1} // revision index
                          >
                            {revision.currentRevisionNumber}
                          </Option>
                        ))}
                    </Select>
                  )}
                </FormItem>
              </div>
            </div>
          )}
          <div className="row">
            <div className="col-12">
              <FormItem label="Description">
                {getFieldDecorator('description', {
                  initialValue: estimateInfo ? estimateInfo.description : null
                })(<TextArea placeholder="Type in description..." rows={4} />)}
              </FormItem>
            </div>
          </div>
          <div className="row" style={{ marginBottom: 20 }}>
            <div className="col-md-5">
              <Button onClick={() => this.handleAddSection()}> Add Section</Button>
              {estimateInfo &&
                (loadingRevision ? (
                  <Spin size="small" className="marginLeft5" />
                ) : (
                  <a
                    href={`${API_BASE}/api/${this.props.apiPath}/print/${
                      this.props.currentId
                    }?revision=${currentRevisionNumberPrint}`}
                    target="_blank"
                  >
                    <Button icon="printer" className="marginLeft5" />
                  </a>
                ))}
              <Popconfirm
                title="Are you sure you want to delete this record?"
                onConfirm={this.props.handleDelete}
                onCancel={this.cancelDelete}
                okText="Yes"
                cancelText="No"
              >
                {this.props.estimateInfo && (
                  <Button type="danger" icon="delete" className="marginLeft5" />
                )}
              </Popconfirm>
            </div>
          </div>
        </Form>
        <div className="row">
          <div className="col-12">
            {this.state.chargedTemplate &&
              this.state.dataStructure &&
              this.state.dataStructure.length > 0 && (
                <div className="row">
                  <div className="col-12">
                    {this.state.dataStructure.map((section, index) => {
                      return (
                        !section.markedForDeletion && (
                          <EstimateSection
                            key={index}
                            section={section}
                            sectionIndex={index}
                            items={items}
                            handleDuplicateSection={this.handleOpenDuplicateSectionModal}
                            handleItemChange={this.handleItemChange}
                            handleSectionDown={this.handleSectionDown}
                            handleSectionUp={this.handleSectionUp}
                            handleSectionDelete={this.handleSectionDelete}
                            handleItemUp={this.handleItemUp}
                            handleItemDown={this.handleItemDown}
                            handleAddItem={this.handleAddItem}
                            handleItemDelete={this.handleItemDelete}
                            handleItemColumnChange={this.handleItemColumnChange}
                            handleSectionNameChange={this.handleSectionNameChange}
                            defaultOverhead={_.get(this.props, 'estimateInfo.overhead', 0)}
                            defaultTax={_.get(this.props, 'estimateInfo.tax', 0)}
                            defaultMarkup={_.get(this.props, 'estimateInfo.markup', 0)}
                          />
                        )
                      )
                    })}
                  </div>
                </div>
              )}
          </div>
        </div>
        <div className="row">
          <div className="col-md-10 text-right">
            <h6>Total:</h6>
            <h6>Overhead:</h6>
            <h6>Markup:</h6>
            <h6>Tax:</h6>
            <h5>Grand Total:</h5>
          </div>
          <div className="col-md-2">
            <h6>
              <b>{numeral(this.state.totals && this.state.totals.total).format('$0,0.00')}</b>
            </h6>
            <h6>
              <b>
                {numeral(this.state.totals && this.state.totals.overhead_total).format('$0,0.00')}
              </b>
            </h6>
            <h6>
              <b>
                {numeral(this.state.totals && this.state.totals.markup_total).format('$0,0.00')}
              </b>
            </h6>
            <h6>
              <b>{numeral(this.state.totals && this.state.totals.tax_total).format('$0,0.00')}</b>
            </h6>
            <h5>
              <b>{numeral(this.state.totals && this.state.totals.grand_total).format('$0,0.00')}</b>
            </h5>
          </div>
        </div>
        <Modal
          visible={this.state.modalSectionNameVisible}
          title={'Clone Section'}
          onCancel={this.handleCancelSectionNameModal}
          onOk={this.handleDuplicateSection}
          width="40%"
          maskClosable={false}
        >
          <div className="jobDetailsHeader">Section Name</div>
          <Input
            value={this.state.sectionName}
            onChange={e => this.setState({ sectionName: e.target.value })}
          />
        </Modal>
      </div>
    )
  }
}
EstimateForm.propTypes = propTypes
export default Form.create()(EstimateForm)



// WEBPACK FOOTER //
// ./src/components/estimate/EstimateForm.js